import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link, useRouteMatch, useParams } from "react-router-dom"
import { Table, Tooltip, Skeleton, notification, Button, Dropdown, Menu } from "antd"
import { DownloadOutlined, PlusOutlined, UploadOutlined, EditOutlined, SearchOutlined, FilterOutlined, DownOutlined, EyeOutlined, DeleteOutlined } from "@ant-design/icons"
import nodePath from "path"
import moment from "moment"

import { fetchSupplyAccounts, selectSupplyAccounts, deleteSupplyAccount, selectTotal, selectPagination, selectLoading, filterSupplyAccounts, generateCsv, selectFilters, selectSort, setFilters, setSort } from "../supplyAccountsSlice"
import { allObjectValuesEmpty } from "../../../functions/objectFunctions/allObjectValuesEmpty"
import ColumnSearchFilter from "../../../components/utilityComponents/table/ColumnSearchFilter"
import ColumnToggleFilter from "../../../components/utilityComponents/table/ColumnToggleFilter"
import ColumnHeaderTitle from "../../../components/utilityComponents/table/ColumnHeaderTitle"
import { tableFiltersApplied } from "../../../functions/objectFunctions/tableFiltersApplied"
import ColumnDateFilter from "../../../components/utilityComponents/table/ColumnDateFilter"
import useConfirmationModal from "../../alertsPanel/ShowConfirm"

import Role from "../../userRoles/Role"
import { getIcon } from "../../../lib/utilities"

export default function SupplyAccountTable({ scopedByClient }) {
    /////*** react router start ***/////
    const { url } = useRouteMatch()
    let { clientId, facilityId } = useParams()
    /////*** react router end ***/////

    /////*** redux start ***/////
    const dispatch = useDispatch()
    const supplyAccounts = useSelector(selectSupplyAccounts)
    const total = useSelector(selectTotal)
    const pagination = useSelector(selectPagination)
    const loading = useSelector(selectLoading)
    const filters = useSelector(selectFilters)
    const sort = useSelector(selectSort)
    /////*** redux end ***/////

    /////*** useState start ***/////
    const [loaded, setLoaded] = useState(false)
    // const [filters, setFilters] = useState({})
    // const [sort, setSort] = useState({
    //     column: "name",
    //     direction: "asc",
    // })
    /////*** useState end ***/////

    /////*** useEffects start ***/////
    useEffect(() => {
        if (!loaded) {
            setLoaded(true)
            handleFetchInitialSupplyAccounts(true)
        }
    }, [loaded, url, dispatch, pagination]) // eslint-disable-line
    /////*** useEffects end ***/////

    /////*** event handler methods start ***/////
    const handleFetchInitialSupplyAccounts = (withFilters = false) => {
        if(!withFilters){
            dispatch(fetchSupplyAccounts(pagination, nodePath.join(url, "")))
        }
        else{
            dispatch(
                filterSupplyAccounts(
                    {
                        clientId: clientId,
                        filters: filters,
                        pagination: pagination,
                        sort: sort,
                    },
                    { facilityId: facilityId }
                )
            )
        }
    }

    const handleFilterSupplyAccounts = (newPagination = null, newSort = null) => {
        return dispatch(
            filterSupplyAccounts(
                {
                    clientId: clientId,
                    filters: filters,
                    pagination: !!newPagination ? newPagination : pagination,
                    sort: !!newSort ? newSort : sort,
                },
                { facilityId: facilityId }
            )
        )
    }

    const showConfirm = useConfirmationModal();
    const handleDeleteClick = (id) => {
        showConfirm(deleteSupplyAccount, id);
    };

    const handleSortSupplyAccountsChange = (column) => () => {
        let newDirection = sort.direction === "asc" ? "desc" : "asc"
        let newSort = {
            column: column,
            direction: newDirection,
        }
        dispatch(setSort(newSort))
        handleFilterSupplyAccounts(null, newSort)
    }

    const handleTableChange = (newPagination, sorter) => {
        if (allObjectValuesEmpty(filters) && allObjectValuesEmpty(sorter)) {
            dispatch(fetchSupplyAccounts(newPagination, nodePath.join(url, "")))
        } else {
            handleFilterSupplyAccounts(newPagination)
        }
    }
    /////*** event handler methods end ***/////

    const renderColumnHeaderTitle = (title) => {
        return <ColumnHeaderTitle title={title} handleSortChange={handleSortSupplyAccountsChange} sort={sort} />
    }

    const renderResetFilters = () => {
        if (allObjectValuesEmpty(filters)) {
            return null
        }
        return (
            <div className={"actions-alternate-buttons"}>
                <button
                    // href="javascript:;"
                    onClick={() => {
                        dispatch(setFilters({}))
                        dispatch(setSort({ column: "name", direction: "asc" }))
                        dispatch(
                            filterSupplyAccounts(
                                {
                                    clientId: clientId,
                                    filters: {},
                                    pagination: { current: 1, pageSize: 10 },
                                    sort: { column: "name", direction: "asc" },
                                },
                                { facilityId: facilityId }
                            )
                        )
                    }}>
                    <FilterOutlined />
                    Reset Filters
                </button>
            </div>
        )
    }

    const renderSearchOutlinedButton = (filterKey) => {
        return <SearchOutlined style={{ fontSize: "16px", color: tableFiltersApplied(filterKey, filters) ? "#1890ff" : undefined }} />
    }

    const supplyAccountCsv = () => {
        notification.open({
            message: "Supply account export started",
            duration: 0.8,
        })

        dispatch(generateCsv(clientId, filters))
    }

    // console.log(supplyAccounts)
    return (
        <div className="muc-table card">
            <div className="card-body">
                <div>
                    <div className="card-body-heading">
                        <div className="badged-header">
                            <h2>Supply Accounts</h2>
                            <div className="badge">{total}</div>
                        </div>
                        <div className="actions">
                            <div className="table-action-links">
                                {renderResetFilters()}

                                <Role action="create" model="supply_account">
                                    <Link to={scopedByClient ? `/clients/${scopedByClient}/supply_accounts/new` : `/supply_accounts/new`}>
                                        <PlusOutlined />
                                        Add New
                                    </Link>
                                </Role>

                                <Link to={`${url}/import`}>
                                    <PlusOutlined />
                                    Import
                                </Link>

                                <a href="javascript:;" onClick={() => supplyAccountCsv()}>
                                    <DownloadOutlined />
                                    Export
                                </a>
                            </div>
                        </div>
                    </div>

                    <Skeleton active loading={loading}>
                        <Table
                            dataSource={supplyAccounts}
                            pagination={{ ...pagination, showSizeChanger: true, total: total, pageSizeOptions: [10, 20, 50, 100, 200] }}
                            onChange={handleTableChange}
                            rowKey={(r) => r.id}>
                            <Table.Column
                                title={renderColumnHeaderTitle("name")}
                                key="name"
                                dataIndex="name"
                                filterDropdown={({ visible }) => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchInitialSupplyAccounts}
                                        filterAction={filterSupplyAccounts}
                                        filterColumn={"name"}
                                        filterType={"name_cont"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        facilityId={facilityId}
                                        visible={visible}
                                        useDispatch
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("name_cont")}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("facility")}
                                key="facility_id"
                                dataIndex="facility"
                                render={(facility) => facility?.name}
                                filterDropdown={({ visible }) => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchInitialSupplyAccounts}
                                        filterAction={filterSupplyAccounts}
                                        filterColumn={"facility_name"}
                                        filterType={"facility_name_cont"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        facilityId={facilityId}
                                        visible={visible}
                                        useDispatch
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("facility_name_cont")}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("account_number")}
                                key="account_number"
                                dataIndex="account_number"
                                filterDropdown={({ visible }) => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchInitialSupplyAccounts}
                                        filterAction={filterSupplyAccounts}
                                        filterColumn={"account_number"}
                                        filterType={"account_number_cont"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        facilityId={facilityId}
                                        visible={visible}
                                        useDispatch
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("account_number_cont")}
                            />
                            <Table.Column
                                // title={renderColumnHeaderTitle("tax_id")}
                                title={"Tax id(s)"}
                                key="tax_ids"
                                dataIndex="tax_ids"
                                render={(text) => {
                                    if (text && text[0] && text[0] != "none") return text.join(", ")
                                }}
                                // filterDropdown={({ visible }) => (
                                //     <ColumnSearchFilter
                                //         handleFetchInitial={handleFetchInitialSupplyAccounts}
                                //         filterAction={filterSupplyAccounts}
                                //         filterColumn={"tax_id"}
                                //         filterType={"tax_id_cont"}
                                //         filters={filters}
                                //         pagination={pagination}
                                //         setFilters={setFilters}
                                //         sort={sort}
                                //         setSort={setSort}
                                //         facilityId={facilityId}
                                //         visible={visible}
                                //         useDispatch
                                //     />
                                // )}
                                // filterIcon={() => renderSearchOutlinedButton("tax_id_cont")}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("vendor")}
                                key="vendor_id"
                                dataIndex="vendor"
                                render={(vendor) => vendor?.name}
                                filterDropdown={({ visible }) => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchInitialSupplyAccounts}
                                        filterAction={filterSupplyAccounts}
                                        filterColumn={"vendor_name"}
                                        filterType={"vendor_name_cont"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        facilityId={facilityId}
                                        visible={visible}
                                        useDispatch
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("vendor_name_cont")}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("active")}
                                key="active"
                                dataIndex="active"
                                render={(active) => (active ? "Active" : "Inactive")}
                                filterDropdown={({ visible }) => (
                                    <ColumnToggleFilter
                                        handleFetchInitial={handleFetchInitialSupplyAccounts}
                                        filterAction={filterSupplyAccounts}
                                        filterColumn={"active"}
                                        filterType={"active_eq"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        facilityId={facilityId}
                                        visible={visible}
                                        useDispatch
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("active_eq")}
                            />
                            {/* <Table.Column
                                title="Linked Service(s)"
                                key="formated_linked_service_names"
                                dataIndex="formated_linked_service_names"
                            /> */}
                            <Table.Column
                                title="Linked Service(s)"
                                key="service_identifiers"
                                dataIndex="service_identifiers"
                                render={(text) => {
                                    let newText = ""
                                    for (let i = 0; i < text?.length; i++) {
                                        newText += text[i][1]
                                        if (i < text.length - 1) {
                                            newText += ", "
                                        }
                                    }
                                    if (newText) return newText
                                }}
                                filterDropdown={({ visible }) => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchInitialSupplyAccounts}
                                        filterAction={filterSupplyAccounts}
                                        filterColumn={"service_identifiers"}
                                        filterType={"linked_services_service_billable_account_vendor_name_or_linked_services_service_meter_number_or_linked_services_service_billable_account_account_number_or_linked_services_service_id_number_cont"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        facilityId={facilityId}
                                        visible={visible}
                                        useDispatch
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("linked_services_service_billable_account_vendor_name_or_linked_services_service_meter_number_or_linked_services_service_billable_account_account_number_or_linked_services_service_id_number_cont")}
                            />
                            {/* <Table.Column title="Service(s)" key="services" dataIndex="services" /> */}
                            <Table.Column
                                title="Service(s)"
                                key="service_types"
                                dataIndex="service_types"
                                render={(text, record) => {
                                    // console.log(text)
                                    // const services = text.split(',')
                                    return text?.map((s, i) => <Tooltip title={`${s.name} (${s.symbol})`}>{getIcon(s.name)}</Tooltip>)
                                }}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("last_bill_date")}
                                // title={"Last Bill Date"}
                                key="last_bill_date"
                                dataIndex="last_bill_date"
                                filterDropdown={({ visible }) => (
                                    <ColumnDateFilter
                                        handleFetchInitial={handleFetchInitialSupplyAccounts}
                                        filterAction={filterSupplyAccounts}
                                        filterColumn={"last_bill_date"}
                                        filterType={"last_bill_date"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        facilityId={facilityId}
                                        visible={visible}
                                        useDispatch
                                        // filterOpen={filterDropdownOpen}
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("last_bill_date_lteq" || "last_bill_date_gteq")}
                                render={(text, record, index) => (record.last_bill_date ? moment(record.last_bill_date).format("L") : "")}
                            />
                            <Table.Column
                                title="Copy"
                                key="last_bill_file_url"
                                dataIndex="last_bill_file_url"
                                render={(text, record) => {
                                    if (text) {
                                        return (
                                            <Button type="primary" size="small" onClick={() => window.open(text, "_blank")}>
                                                <DownloadOutlined />
                                            </Button>
                                        )
                                    }
                                }}
                            />
                            <Table.Column
                                title="Edit"
                                key="action"
                                render={(text, record) => (
                                    <>
                                        <Dropdown
                                            overlay={
                                                <Menu>
                                                    <Role action="update" model="supply_account">
                                                        <Menu.Item key="1" icon={<EditOutlined />}>
                                                            <Link to={scopedByClient ? `/clients/${scopedByClient}/supply_accounts/${record.id}/edit` : `/supply_accounts/${record.id}/edit`}>
                                                                Edit Account
                                                            </Link>
                                                        </Menu.Item>
                                                    </Role>

                                                    <Menu.Item key="2" icon={<EyeOutlined />}>
                                                        <Link to={`/clients/${scopedByClient}/supply_accounts/${record.id}/dashboard`}>View</Link>
                                                    </Menu.Item>

                                                    <Role action="destroy" model="supply_account">
                                                        <Menu.Item key="3" icon={<DeleteOutlined />} onClick={() => handleDeleteClick(record.id)}>
                                                            Delete Account
                                                        </Menu.Item>
                                                    </Role>
                                                </Menu>
                                            }>
                                            <Button>
                                                Actions <DownOutlined />
                                            </Button>
                                        </Dropdown>
                                    </>
                                )}
                            />
                        </Table>
                    </Skeleton>
                </div>
            </div>
            {/* <div className="card-footer">
                <div className="card-footer-left"></div>
                <div className="card-footer-right pagination"></div>
            </div> */}
        </div>
    )
}
